/**
 * Anteprima del modulo contatti nel compositore.
 *
 * La bozza del compositore puo' contenere campi a meta' (senza etichetta, con
 * una sola opzione, consenso senza testo). Al salvataggio il server li scarta
 * (services/contactForm.js, normalizeFormDefinition): l'anteprima applica le
 * stesse regole, cosi' mostra quello che il visitatore vedra' davvero, e conta
 * a parte i campi incompleti.
 */

const DEFAULTS = {
  title: "Ti ricontattiamo noi",
  submit_label: "Invia",
  thank_you: "Grazie, abbiamo ricevuto la tua richiesta.",
  trigger_label: "Fatti ricontattare",
};

const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

function previewField(raw, index) {
  const type = raw?.type;
  const label = clean(raw?.label);
  // Stesso identificativo usato dal compositore per i campi non ancora salvati.
  const id = raw?.id || raw?._key || `campo_${index + 1}`;
  const base = { id, type, label, required: Boolean(raw?.required) };

  if (type === "text") {
    if (!label) return null;
    const mode = ["free", "email", "phone"].includes(raw.mode) ? raw.mode : "free";
    return {
      ...base,
      mode,
      placeholder: clean(raw.placeholder),
      multiline: mode === "free" && Boolean(raw.multiline),
    };
  }

  if (type === "single_choice" || type === "multi_choice") {
    const options = [...new Set((raw.options || []).map(clean).filter(Boolean))];
    if (!label || options.length < 2) return null;
    if (type === "single_choice") return { ...base, options };

    const n = options.length;
    const rawMin = Number(raw.min);
    const rawMax = Number(raw.max);
    const min = Math.min(Math.max(Number.isFinite(rawMin) ? Math.trunc(rawMin) : 0, 0), n);
    const max = Math.min(Math.max(Number.isFinite(rawMax) ? Math.trunc(rawMax) : n, Math.max(min, 1)), n);
    const required = base.required || min >= 1;
    return { ...base, options, required, min: required ? min : 0, max };
  }

  if (type === "file") {
    if (!label) return null;
    const accept = ["both", "images", "documents"].includes(raw.accept) ? raw.accept : "both";
    return { ...base, accept };
  }

  if (type === "consent") {
    const text = clean(raw.text);
    if (!text) return null;
    const url = clean(raw.link_url);
    const link_url = /^https?:\/\//i.test(url) ? url : "";
    return {
      ...base,
      text,
      link_url,
      link_label: link_url ? clean(raw.link_label) || "Scopri di più" : "",
    };
  }

  return null;
}

/**
 * @param {object} form bozza del compositore
 * @returns {{ card: object, incomplete: number, triggerEnabled: boolean, triggerLabel: string }}
 */
export function previewFromDraft(form = {}) {
  const rawFields = Array.isArray(form?.fields) ? form.fields : [];
  const fields = rawFields.map(previewField).filter(Boolean);

  return {
    card: {
      kind: "contact_form",
      id: "pingup-contact-form-preview",
      title: clean(form?.title) || DEFAULTS.title,
      description: clean(form?.description),
      submit_label: clean(form?.submit_label) || DEFAULTS.submit_label,
      thank_you: clean(form?.thank_you) || DEFAULTS.thank_you,
      fields,
      topic: "",
    },
    incomplete: rawFields.length - fields.length,
    triggerEnabled: form?.trigger_enabled !== false,
    triggerLabel: clean(form?.trigger_label) || DEFAULTS.trigger_label,
  };
}

const HEX = /^#?([0-9a-f]{6})$/i;

function luminance(hex) {
  const m = HEX.exec(hex || "");
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

/**
 * Le variabili CSS del widget a partire dal tema del bot, come le calcola
 * ChatApp.jsx: il modulo in anteprima ha gli stessi colori, raggio e carattere.
 */
export function widgetVarsFromTheme(theme = {}) {
  const t = theme || {};
  const primary = HEX.test(t.primaryColor || "") ? t.primaryColor : "#2563eb";
  const dark = Boolean(t.darkMode);
  const bg = dark ? "#1f2937" : t.backgroundColor || "#ffffff";
  const text = dark ? "#f3f4f6" : t.textColor || "#1f2937";
  const radius = Number.isFinite(Number(t.borderRadius)) ? Number(t.borderRadius) : 8;
  const fontSize = Number.isFinite(Number(t.fontSize)) ? Number(t.fontSize) : 14;
  const font = String(t.fontFamily || "Inter, system-ui, sans-serif").replace(/[;{}<>]/g, "");
  const onPrimary = luminance(primary) > 0.65 ? "#020617" : "#ffffff";

  // Anche queste vanno impostate: la dashboard definisce --border in formato
  // shadcn ("214 32% 91%"), la variabile attraversa lo shadow DOM e senza un
  // valore proprio i campi dell'anteprima restavano senza bordo.
  return [
    `--bg-input:${dark ? "#111827" : "#ffffff"}`,
    `--border:${dark ? "#4b5563" : "#e5e7eb"}`,
    `--text-muted:${dark ? "#9ca3af" : "#6b7280"}`,
    `--primary:${primary}`,
    `--bg:${bg}`,
    `--text:${text}`,
    `--radius:${radius}px`,
    `--font:${font}`,
    `--font-size:${fontSize}px`,
    `--bg-button:${primary}`,
    `--on-primary:${onPrimary}`,
  ].join(";");
}
